import { AgGridSortDirection } from '../data-grid/sort-direction';
import { NumberFormat, NumberUnit } from '../numbers';
import { IAxis } from './axis';
import { TableSortType } from './table-sort-type';
import { VisualizationType } from './visualization-type';
import { VizConfigs } from './viz-configs';
import { ZoomLevelInfo } from './zoom-level-info';

// TODO: move to lib-visualizations
export class VisualizationConfigs {
    id?: number;
    isDefault?: boolean;
    visualizationType: VisualizationType;
    enableTooltip?: boolean;
    enableLabels?: boolean | null;
    labelStyle?: 'showLines' | 'hideLines' | 'inline' | null;
    enableInsideLabel?: boolean;
    labelOrientation?: 'horizontal' | 'vertical' | null;
    enableTotalLabel?: boolean | null;
    highlightSlice?: boolean;
    inChartLabel?: boolean;
    showBrush?: boolean;
    zoomLevels?: ZoomLevelInfo[];
    showGridLines?: boolean;
    gridLineStyle?: 'solid-line' | 'dotted-line' | 'none';
    axisConfigurations: IAxis[];
    showHoverLines?: boolean;
    numberUnits?: NumberUnit;
    numberFormat?: NumberFormat;
    decimalPlaces?: number;
    enableLegend?: boolean;
    legendPosition?: 'top' | 'right' | 'bottom';
    displayWidgetName?: boolean;
    enableSliceManagement?: boolean;
    sliceManagementType?: 'percentage' | 'maxCount';
    groupByPercent?: number;
    groupByMaxCount?: number;
    showOthers?: boolean;
    bottomXAxisOrientation?: string;
    leftYAxisLabels?: boolean;
    configs?: VizConfigs;
    sortTableBy?: TableSortType;
    tableSortDirection?: 'ASC' | 'DESC' | 'CUSTOM';
    sortOrderSlicer?: string;

    // Used for Grid only
    showGrandTotal?: boolean | null;
    showTotalAtTop?: boolean | null;
    showSubTotal?: boolean | null;
    showSubTotalAtTop?: boolean | null;
    alternateRowShading?: boolean | null;
    rowHeight?: 20 | 25 | 30 | 40 | null;
    showHyperlinks?: boolean | null;
    repeatGroupData?: boolean | null;
    autoSizeColumns?: boolean | null;
    showColumnHeaderTooltip?: boolean | null;
    isPivotMode?: boolean | null;
    hideGridHeaders?: boolean;
    showQuickSearch?: boolean;
    distinctRowsOnly?: boolean;
    groupDefaultExpanded?: number | null;
    groupSort?: AgGridSortDirection | null;
    groupSortIndex?: number | null;
    customWidthWhenGroup?: number;
    displayTotal?: boolean | null;
    displayTotalLabel?: string;
    showGrouperCount?: boolean | null;
    autoAddDynamicColumns?: boolean | null;
    useCustomHeaderNames?: boolean | null;

    // eslint-disable-next-line complexity
    constructor(init?: Partial<VisualizationConfigs>) {
        this.id = init?.id;
        this.isDefault = init?.isDefault;
        this.visualizationType = init?.visualizationType ?? 'SIMPLE_GRID';
        this.enableTooltip = init?.enableTooltip;
        this.enableLabels = init?.enableLabels;
        this.labelStyle = init?.labelStyle;
        this.enableInsideLabel = init?.enableInsideLabel;
        this.labelOrientation = init?.labelOrientation;
        this.enableTotalLabel = init?.enableTotalLabel;
        this.highlightSlice = init?.highlightSlice;
        this.inChartLabel = init?.inChartLabel;
        this.showBrush = init?.showBrush;
        this.zoomLevels = init?.zoomLevels ?? [];
        this.showGridLines = init?.showGridLines;
        this.gridLineStyle = init?.gridLineStyle;
        this.axisConfigurations = init?.axisConfigurations ?? [];
        this.showHoverLines = init?.showHoverLines;
        this.numberUnits = init?.numberUnits;
        this.numberFormat = init?.numberFormat;
        this.decimalPlaces = init?.decimalPlaces;
        this.enableLegend = init?.enableLegend;
        this.legendPosition = init?.legendPosition;
        this.displayWidgetName = init?.displayWidgetName;
        this.enableSliceManagement = init?.enableSliceManagement;
        this.sliceManagementType = init?.sliceManagementType;
        this.groupByPercent = init?.groupByPercent;
        this.groupByMaxCount = init?.groupByMaxCount;
        this.showOthers = init?.showOthers ?? true;
        this.bottomXAxisOrientation = init?.bottomXAxisOrientation;
        this.leftYAxisLabels = init?.leftYAxisLabels;
        this.configs = init?.configs;
        this.sortTableBy = init?.sortTableBy;
        this.tableSortDirection = init?.tableSortDirection;
        this.sortOrderSlicer = init?.sortOrderSlicer;
        this.showGrandTotal = init?.showGrandTotal;
        this.showTotalAtTop = init?.showTotalAtTop;
        this.showSubTotal = init?.showSubTotal;
        this.showSubTotalAtTop = init?.showSubTotalAtTop;
        this.alternateRowShading = init?.alternateRowShading;
        this.rowHeight = init?.rowHeight;
        this.showHyperlinks = init?.showHyperlinks;
        this.repeatGroupData = init?.repeatGroupData;
        this.autoSizeColumns = init?.autoSizeColumns;
        this.showColumnHeaderTooltip = init?.showColumnHeaderTooltip;
        this.isPivotMode = init?.isPivotMode;
        this.hideGridHeaders = init?.hideGridHeaders;
        this.showQuickSearch = init?.showQuickSearch;
        this.distinctRowsOnly = init?.distinctRowsOnly;
        this.groupDefaultExpanded = init?.groupDefaultExpanded;
        this.groupSort = init?.groupSort;
        this.groupSortIndex = init?.groupSortIndex;
        this.customWidthWhenGroup = init?.customWidthWhenGroup;
        this.displayTotal = init?.displayTotal;
        this.displayTotalLabel = init?.displayTotalLabel;
        this.showGrouperCount = init?.showGrouperCount;
        this.autoAddDynamicColumns = init?.autoAddDynamicColumns;
        this.useCustomHeaderNames = init?.useCustomHeaderNames;

        this.configs = new VizConfigs(this.configs);
    }

    // the ? is here so that we can pass things that aren't actually classes of type around as
    // though they are.  for example, something like { visualizationType: 'foo' }
    // doing this is, of course, incredibly stupid
    isAdvancedGrid?(): boolean {
        return this.visualizationType === 'ADVANCED_GRID';
    }
}
